import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { DEFAULT_LOGO, getClubLogo } from '../clubLogos'
import { getFormation, allowsSmallSided } from '../utils/formations'
import EventTable from '../components/EventTable'
import SquadTab from '../components/SquadTab'
import PitchTab from '../components/PitchTab'
import PitchDisplay from '../components/PitchDisplay'
import ScoreTab from '../components/ScoreTab'
import OppositionTab from '../components/OppositionTab'

const POSITION_NUMBERS = {
  football: {
    1: 'Goalkeeper', 2: 'Right Corner Back', 3: 'Full Back', 4: 'Left Corner Back',
    5: 'Right Half Back', 6: 'Centre Back', 7: 'Left Half Back',
    8: 'Midfield', 9: 'Midfield', 10: 'Right Half Forward', 11: 'Centre Forward',
    12: 'Left Half Forward', 13: 'Right Corner Forward', 14: 'Full Forward', 15: 'Left Corner Forward'
  },
  hurling: {
    1: 'Goalkeeper', 2: 'Right Corner Back', 3: 'Full Back', 4: 'Left Corner Back',
    5: 'Right Half Back', 6: 'Centre Back', 7: 'Left Half Back',
    8: 'Midfield', 9: 'Midfield', 10: 'Right Half Forward', 11: 'Centre Forward',
    12: 'Left Half Forward', 13: 'Right Corner Forward', 14: 'Full Forward', 15: 'Left Corner Forward'
  }
}
function getSourceLabel(notes, sport) {
  if (!notes) return ''
  if (notes === 'from_play') return 'Play'
  if (notes === 'free') return 'Free'
  if (notes === 'sideline') return 'Sideline'
  if (notes === '45' || notes === '65') return sport === 'hurling' ? '65' : '45'
  if (notes === 'penalty') return 'Penalty'
  return ''
}

export default function MatchTracker({ fixture, profile, onBack }) {
  const [players, setPlayers] = useState([])
  const [lineup, setLineup] = useState([])
  const [subs, setSubs] = useState([])
  const [substitutedOff, setSubstitutedOff] = useState([])
  const [redCardedPlayers, setRedCardedPlayers] = useState([])
  const [blackCardPlayers, setBlackCardPlayers] = useState([])
  const [oppBlackCardPlayers, setOppBlackCardPlayers] = useState([])
  const [events, setEvents] = useState([])
  const [seconds, setSeconds] = useState(0)
  const [running, setRunning] = useState(false)
  const [half, setHalf] = useState(1)
  const [modal, setModal] = useState(null)
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [subStep, setSubStep] = useState(1)
  const [subOff, setSubOff] = useState(null)
  const [multiSubMode, setMultiSubMode] = useState(false)
  const [multiSubsOff, setMultiSubsOff] = useState([])
  const [multiSubsOn, setMultiSubsOn] = useState([])
  const [subInitiatedBySub, setSubInitiatedBySub] = useState(false)
  const [swapFirst, setSwapFirst] = useState(null)
  const [previousSelection, setPreviousSelection] = useState([])
  const [subsUsed, setSubsUsed] = useState(0)
  const [ageRules, setAgeRules] = useState(null)
  const [activeTab, setActiveTab] = useState('score')
  const [pendingScoreType, setPendingScoreType] = useState(null)
  const [pendingScoreSource, setPendingScoreSource] = useState(null)
  const [pendingOppJersey, setPendingOppJersey] = useState(null)
  const [scoringLocked, setScoringLocked] = useState(false)
  const [resetModal, setResetModal] = useState(false)
  const [undoModal, setUndoModal] = useState(false)
  const [trackedBy, setTrackedBy] = useState(null)
  const [trackedByName, setTrackedByName] = useState(null)
  const [score, setScore] = useState({
    hg: fixture.home_goals || 0, hp: fixture.home_points || 0,
    ag: fixture.away_goals || 0, ap: fixture.away_points || 0
  })
  const [matchPhase, setMatchPhase] = useState(fixture.match_phase || null)

  const intervalRef = useRef(null)
  const syncIntervalRef = useRef(null)
  const eventsIntervalRef = useRef(null)
  const wakeLockRef = useRef(null)
  const touchStartX = useRef(null)
  const secondsRef = useRef(0)
  const runningRef = useRef(false)
  const halfRef = useRef(1)
  const isTrackerRef = useRef(false)

  const sport = fixture.sport || 'football'
  const isHurl = sport === 'hurling' || sport === 'camogie'
  const myClubLogo = getClubLogo(profile?.clubs?.club_name || profile?.clubs?.name)
  const koLabel = isHurl ? 'Puck Out' : 'Kick Out'
  const koLabelPlural = isHurl ? 'Puck Outs' : 'Kick Outs'
  const setPieceLabel = isHurl ? '65' : '45'
  const isFriendly = fixture.game_type === 'friendly'
  const isRollingSubsGrade = ageRules ? ageRules.rolling_subs : true
  const isUnlimitedSubs = isRollingSubsGrade || isFriendly
  const maxSubs = ageRules?.max_subs_normal_time ?? (isUnlimitedSubs ? 999 : 5)
  const allowsTwoPointFootball = ageRules ? ageRules.allows_two_point_football : true
  const blackCardTempSub = ageRules ? ageRules.black_card_temp_sub : false
  const ladiesFootball = (fixture.squads?.gender === 'girls') && (sport === 'football')
  const sinBinUsesYellow = ageRules ? ageRules.sin_bin_uses_yellow : false
  const sinBinMins = ageRules?.sin_bin_mins ?? 10
  const MAX_HALF_SECONDS = 50 * 60
  const tabs = ['score', 'pitch', 'squad', 'events', 'opposition']

  // Computed: is this device the tracker?
  const isTracker = !trackedBy || trackedBy === profile?.id
  const isAdminRole = profile?.role === 'club_admin' || profile?.role === 'squad_admin'


  // Keep screen awake while match tracker is open
  useEffect(() => {
    async function requestWakeLock() {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen')
        }
      } catch (e) {
        console.log('Wake lock failed:', e)
      }
    }
    requestWakeLock()

    // Re-acquire wake lock if page becomes visible again
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        await requestWakeLock()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (wakeLockRef.current) {
        wakeLockRef.current.release()
        wakeLockRef.current = null
      }
    }
  }, [])
  // ── Clock sync ──────────────────────────────────────────────────────────────

  useEffect(() => {
    loadClockFromDb()
    loadPlayers()
    loadEvents()
    loadAgeRules()

    // Poll every 10s for clock + score
    syncIntervalRef.current = setInterval(() => {
      if (!isTrackerRef.current) {
        loadClockFromDb()
        loadScoreFromDb()
      }
    }, 10000)

    // Poll every 60s for events + lineup
    eventsIntervalRef.current = setInterval(() => {
      if (!isTrackerRef.current) {
        loadEvents()
        loadPlayers()
      }
    }, 60000)

    return () => {
      clearInterval(intervalRef.current)
      clearInterval(syncIntervalRef.current)
      clearInterval(eventsIntervalRef.current)
    }
  }, [])

// Black card countdown — only ticks when match clock is running
  useEffect(() => {
    if (!running || blackCardPlayers.length === 0) return
    const interval = setInterval(() => {
      setBlackCardPlayers(prev => {
        const updated = prev.map(p => ({
          ...p,
          secondsRemaining: Math.max(0, (p.secondsRemaining ?? 600) - 1)
        }))
        // Remove players whose time is up
        return updated.filter(p => p.secondsRemaining > 0)
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [running, blackCardPlayers.length])
  
  // Opp black card countdown
  useEffect(() => {
    if (!running || oppBlackCardPlayers.length === 0) return
    const interval = setInterval(() => {
      setOppBlackCardPlayers(prev => {
        const updated = prev.map(p => ({ ...p, secondsRemaining: Math.max(0, (p.secondsRemaining ?? 600) - 1) }))
        return updated.filter(p => p.secondsRemaining > 0)
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [running, oppBlackCardPlayers.length])

  async function loadClockFromDb() {
    const { data } = await supabase.from('fixtures')
      .select('clock_started_at, clock_seconds_at_start, clock_running, clock_half, tracked_by, tracked_by_name')
      .eq('id', fixture.id).single()
    if (!data) return

    const dbHalf = data.clock_half || 1
    const dbRunning = data.clock_running || false
    const dbSecsAtStart = data.clock_seconds_at_start || 0
    const dbStartedAt = data.clock_started_at

    setHalf(dbHalf)
    halfRef.current = dbHalf
    setMatchPhase(data.match_phase || null)
    setTrackedBy(data.tracked_by || null)
    setTrackedByName(data.tracked_by_name || null)
    if (data.tracked_by === profile?.id) isTrackerRef.current = true

    if (dbRunning && dbStartedAt) {
      const elapsed = Math.floor((Date.now() - new Date(dbStartedAt).getTime()) / 1000)
      const currentSecs = Math.min(dbSecsAtStart + elapsed, MAX_HALF_SECONDS)
      secondsRef.current = currentSecs
      setSeconds(currentSecs)


      // Always restart interval to stay in sync
      clearInterval(intervalRef.current)
      runningRef.current = true
      setRunning(true)
      const startTime = Date.now() - currentSecs * 1000
      intervalRef.current = setInterval(() => {
        if (!runningRef.current) return
        const elapsed2 = Math.floor((Date.now() - startTime) / 1000)
        if (elapsed2 >= MAX_HALF_SECONDS) {
          clearInterval(intervalRef.current)
          setRunning(false)
          runningRef.current = false
          secondsRef.current = MAX_HALF_SECONDS
          setSeconds(MAX_HALF_SECONDS)
          return
        }
        secondsRef.current = elapsed2
        setSeconds(elapsed2)
      }, 1000)
    } else {
      // Clock stopped — stop local interval and sync to DB value
      clearInterval(intervalRef.current)
      runningRef.current = false
      setRunning(false)
      secondsRef.current = dbSecsAtStart
      setSeconds(dbSecsAtStart)
    }
  }

  async function loadScoreFromDb() {
    const { data } = await supabase.from('fixtures')
      .select('home_goals, home_points, away_goals, away_points')
      .eq('id', fixture.id).single()
    if (!data) return
    setScore({
      hg: data.home_goals || 0,
      hp: data.home_points || 0,
      ag: data.away_goals || 0,
      ap: data.away_points || 0
    })
  }

  async function saveClockToDb(secs, h, isRunning) {
      const update = {
        clock_seconds_at_start: secs,
        clock_half: h,
        clock_running: isRunning,
        clock_started_at: isRunning ? new Date().toISOString() : null
      }
      if (isRunning) update.match_phase = 'playing'
      await supabase.from('fixtures').update(update).eq('id', fixture.id)
    }

  function startClockInterval() {
    clearInterval(intervalRef.current)
    isTrackerRef.current = true
    setRunning(true)
    runningRef.current = true
    const startTime = Date.now() - secondsRef.current * 1000
    intervalRef.current = setInterval(() => {
      if (!runningRef.current) return
      const elapsed = Math.floor((Date.now() - startTime) / 1000)
      if (elapsed >= MAX_HALF_SECONDS) {
        clearInterval(intervalRef.current)
        setRunning(false)
        runningRef.current = false
        secondsRef.current = MAX_HALF_SECONDS
        setSeconds(MAX_HALF_SECONDS)
        saveClockToDb(MAX_HALF_SECONDS, halfRef.current, false)
        return
      }
      secondsRef.current = elapsed
      setSeconds(elapsed)
      if (elapsed % 10 === 0 && elapsed > 0) saveClockToDb(elapsed, halfRef.current, true)
    }, 1000)
  }

  async function toggleClock() {
    if (running) {
      clearInterval(intervalRef.current)
      setRunning(false)
      runningRef.current = false
      saveClockToDb(secondsRef.current, halfRef.current, false)
    } else {
      // Check if match is already being tracked by someone else
      const { data } = await supabase.from('fixtures')
        .select('tracked_by, tracked_by_name').eq('id', fixture.id).single()
      if (data?.tracked_by && data.tracked_by !== profile?.id) {
        alert('This match is being tracked by ' + data.tracked_by_name + '. Ask a Club Admin or Squad Admin to take over from the Admin panel.')
        return
      }
      // Lock match to this user
      await supabase.from('fixtures').update({
        tracked_by: profile?.id,
        tracked_by_name: profile?.full_name
      }).eq('id', fixture.id)
      setTrackedBy(profile?.id)
      setTrackedByName(profile?.full_name)
      isTrackerRef.current = true
      startClockInterval()
      saveClockToDb(secondsRef.current, halfRef.current, true)
    }
  }

  function resetForSecondHalf() {
      clearInterval(intervalRef.current)
      setRunning(false)
      runningRef.current = false
      setSeconds(0)
      secondsRef.current = 0
      const nextHalf = halfRef.current === 1 ? 2 : 4
      const phase = halfRef.current === 3 ? 'et_ht' : 'ht'
      setHalf(nextHalf)
      halfRef.current = nextHalf
      saveClockToDb(0, nextHalf, false)
      const htUpdate = { match_phase: phase }
      if (phase === 'ht') {
        htUpdate.ht_home_goals = score.hg
        htUpdate.ht_home_points = score.hp
        htUpdate.ht_away_goals = score.ag
        htUpdate.ht_away_points = score.ap
      }
      supabase.from('fixtures').update(htUpdate).eq('id', fixture.id)
  }

  function resetClock() {
    clearInterval(intervalRef.current)
    setRunning(false)
    runningRef.current = false
    setSeconds(0)
    secondsRef.current = 0
    saveClockToDb(0, halfRef.current, false)
  }

// ── Data loading ─────────────────────────────────────────────────────────────
  async function loadAgeRules() {
    const { data: squadData } = await supabase
      .from('squads')
      .select('current_grade, age_group, gender')
      .eq('id', fixture.squad_id)
      .maybeSingle()
    if (!squadData) return
    const grade = squadData.current_grade || squadData.age_group
    if (!grade) return
    const { data } = await supabase
      .from('age_grade_rules')
      .select('*')
      .eq('grade', grade)
      .maybeSingle()
    if (data) setAgeRules(data)
  }

  async function loadPlayers() {
    const { data: lineupData } = await supabase
      .from('lineups').select('*, players(*)')
      .eq('fixture_id', fixture.id)
      .order('position_override', { ascending: true })
    if (lineupData && lineupData.length > 0) {
      const starters = lineupData.filter(l => l.starting).map(l => ({
        ...l.players,
        assigned_number: String(l.position_override || ''),
        match_number: String(l.match_number || l.position_override || ''),
        jersey_number: l.players.jersey_number
      }))
      const subsData = lineupData.filter(l => !l.starting).map(l => ({
        ...l.players,
        assigned_number: String(l.position_override || ''),
        match_number: String(l.match_number || l.position_override || ''),
        jersey_number: l.players.jersey_number
      }))
      setLineup(starters)
      setSubs(subsData)
    } else {
      const { data } = await supabase.from('players').select('*')
        .eq('squad_id', fixture.squad_id).eq('is_active', true).order('jersey_number')
      setPlayers(data || [])
    }
  }

  async function loadEvents() {
    const { data } = await supabase.from('match_events')
      .select('*, players(full_name)')
      .eq('fixture_id', fixture.id)
      .order('minute', { ascending: true })
      .order('created_at', { ascending: true })
    setEvents(data || [])
    if (data) {
      setSubsUsed(data.filter(e => e.event_type === 'sub_on').length)
      const redIds = data.filter(e => e.event_type === 'red_card').map(e => e.player_id)
      const undoneReds = data.filter(e => e.event_type === 'undo_red_card').map(e => e.player_id)
      setRedCardedPlayers(redIds.filter(id => !undoneReds.includes(id)))
    }
  }

  // ── Lineup management ────────────────────────────────────────────────────────

  async function saveLineupToDb(starters, subsArr) {
    await supabase.from('lineups').delete().eq('fixture_id', fixture.id)
    const toInsert = [
      ...starters.filter(p => p).map((p, i) => {
        // position_override must be a valid pitch slot (1-15)
        const pos = parseInt(p.assigned_number)
        const safePos = pos >= 1 && pos <= 15 ? String(pos) : String(i + 1)
        return {
          fixture_id: fixture.id, player_id: p.id,
          starting: true,
          position_override: safePos,
          match_number: p.match_number || p.assigned_number || safePos
        }
      }),
      ...subsArr.map(p => ({
        fixture_id: fixture.id, player_id: p.id,
        starting: false,
        position_override: p.assigned_number || '',
        match_number: p.match_number || p.assigned_number || ''
      }))
    ]
    if (toInsert.length > 0) await supabase.from('lineups').insert(toInsert)
  }

  async function saveLineup(starters, subsSelected) {
    const startersWithMatchNum = starters.filter(Boolean).map(p => ({
      ...p, match_number: p.match_number || p.assigned_number || ''
    }))
    const subsWithMatchNum = subsSelected.map(p => ({
      ...p, match_number: p.match_number || p.assigned_number || ''
    }))
    await saveLineupToDb(startersWithMatchNum, subsWithMatchNum)
    await supabase.from('fixtures').update({ status: 'live', match_phase: null }).eq('id', fixture.id)
    setLineup(startersWithMatchNum)
    setSubs(subsWithMatchNum)
    setPreviousSelection([])
  }

  async function handleEditLineup() {
    if (!window.confirm('Edit the lineup?')) return
    await supabase.from('lineups').delete().eq('fixture_id', fixture.id)
    await supabase.from('fixtures').update({ status: 'upcoming', match_phase: null }).eq('id', fixture.id)
    const { data } = await supabase.from('players').select('*')
      .eq('squad_id', fixture.squad_id).eq('is_active', true).order('jersey_number')
    setPreviousSelection([...lineup, ...subs])
    setPlayers(data || [])
    setLineup([])
    setSubs([])
    setSubstitutedOff([])
  }

  async function swapPositions(playerA, playerB) {
    const newLineup = [...lineup]
    const idxA = newLineup.findIndex(p => p.id === playerA.id)
    const idxB = newLineup.findIndex(p => p.id === playerB.id)
    if (idxA !== -1 && idxB !== -1) {
      const slotA = newLineup[idxA].assigned_number
      const slotB = newLineup[idxB].assigned_number
      newLineup[idxA] = { ...newLineup[idxA], assigned_number: slotB }
      newLineup[idxB] = { ...newLineup[idxB], assigned_number: slotA }
    }
    setLineup(newLineup)
    await saveLineupToDb(newLineup, subs)
    setSwapFirst(null)
  }

  // ── Reset ────────────────────────────────────────────────────────────────────

  async function handleReset(type) {
    if (type === 'time') {
      resetClock()
    } else if (type === 'scores') {
      const newScore = { hg: 0, hp: 0, ag: 0, ap: 0 }
      setScore(newScore)
      await supabase.from('fixtures').update({ home_goals: 0, home_points: 0, away_goals: 0, away_points: 0 }).eq('id', fixture.id)
    } else if (type === 'squad') {
      await supabase.from('lineups').delete().eq('fixture_id', fixture.id)
      await supabase.from('fixtures').update({ status: 'upcoming', match_phase: null }).eq('id', fixture.id)
      const { data } = await supabase.from('players').select('*')
        .eq('squad_id', fixture.squad_id).eq('is_active', true).order('jersey_number')
      setPreviousSelection([...lineup, ...subs])
      setPlayers(data || [])
      setLineup([])
      setSubs([])
    } else if (type === 'events') {
      await supabase.from('match_events').delete().eq('fixture_id', fixture.id)
      setEvents([])
    } else if (type === 'all') {
      clearInterval(intervalRef.current)
      setRunning(false)
      setSeconds(0)
      secondsRef.current = 0
      setHalf(1)
      halfRef.current = 1
      setScore({ hg: 0, hp: 0, ag: 0, ap: 0 })
      await supabase.from('fixtures').update({
        status: 'upcoming', home_goals: 0, home_points: 0, away_goals: 0, away_points: 0,
        clock_running: false, clock_seconds_at_start: 0, clock_half: 1, clock_started_at: null,
        tracked_by: null, tracked_by_name: null, match_phase: null
      }).eq('id', fixture.id)
      await supabase.from('match_events').delete().eq('fixture_id', fixture.id)
      await supabase.from('lineups').delete().eq('fixture_id', fixture.id)
      const { data } = await supabase.from('players').select('*')
        .eq('squad_id', fixture.squad_id).eq('is_active', true).order('jersey_number')
      setPlayers(data || [])
      setLineup([])
      setSubs([])
      setEvents([])
      setTrackedBy(null)
      setTrackedByName(null)
      isTrackerRef.current = false
    }
    setResetModal(false)
  }

  // ── Undo last ────────────────────────────────────────────────────────────────

  async function undoLast(type) {
    if (type === 'wide_us') {
      const last = [...events].reverse().find(e => e.event_type === 'wide' || e.event_type === 'fortyfive_won')
      if (!last) { alert('No wide or ' + setPieceLabel + ' to undo.'); return }
      await supabase.from('match_events').delete().eq('id', last.id)
    } else if (type === 'wide_opp') {
      const last = [...events].reverse().find(e => e.event_type === 'opp_wide' || e.event_type === 'fortyfive_conceded')
      if (!last) { alert('No opposition wide or ' + setPieceLabel + ' to undo.'); return }
      await supabase.from('match_events').delete().eq('id', last.id)
    } else if (type === 'kickout') {
      const last = [...events].reverse().find(e =>
        ['kickout_won', 'kickout_lost', 'opp_kickout_won', 'opp_kickout_lost'].includes(e.event_type)
      )
      if (!last) { alert('No kick out to undo.'); return }
      await supabase.from('match_events').delete().eq('id', last.id)
    } else if (type === 'free') {
      const last = [...events].reverse().find(e =>
        ['free_won', 'free_conceded'].includes(e.event_type)
      )
      if (!last) { alert('No free to undo.'); return }
      await supabase.from('match_events').delete().eq('id', last.id)
    } else if (type === 'sub') {
      const lastSubOn = [...events].reverse().find(e => e.event_type === 'sub_on')
      if (!lastSubOn) { alert('No substitution to undo.'); return }
      const lastSubOff = [...events].reverse().find(e => e.event_type === 'sub_off' && e.minute === lastSubOn.minute)
      await supabase.from('match_events').delete().eq('id', lastSubOn.id)
      if (lastSubOff) await supabase.from('match_events').delete().eq('id', lastSubOff.id)
      const playerOnId = lastSubOn.player_id
      const playerOffId = lastSubOff?.player_id
      if (playerOnId && playerOffId) {
        const playerOn = [...lineup, ...subs].find(p => p.id === playerOnId)
        const playerOff = [...lineup, ...subs].find(p => p.id === playerOffId)
        if (playerOn && playerOff) {
          const slotFilled = playerOn.assigned_number
          const newLineup = lineup.map(p => {
            if (p.id === playerOnId) {
              return { ...playerOff, assigned_number: slotFilled, match_number: playerOff.match_number }
            }
            return p
          })
          const newSubs = subs.filter(p => p.id !== playerOffId)
          newSubs.push({ ...playerOn })
          setLineup(newLineup)
          setSubs(newSubs)
          setSubstitutedOff(prev => prev.filter(p => p.id !== playerOffId))
          setSubsUsed(prev => Math.max(0, prev - 1))
          await saveLineupToDb(newLineup, newSubs)
        }
      }
    } else if (type === 'card') {
      const last = [...events].reverse().find(e =>
        ['yellow_card', 'red_card', 'black_card', 'opp_card'].includes(e.event_type)
      )
      if (!last) { alert('No card to undo.'); return }
      await supabase.from('match_events').delete().eq('id', last.id)
      if (last.event_type === 'red_card' && last.player_id) {
        setRedCardedPlayers(prev => prev.filter(id => id !== last.player_id))
      }
      if (last.event_type === 'black_card' && last.player_id) {
        setBlackCardPlayers(prev => prev.filter(p => p.id !== last.player_id))
      }
    }
    await loadEvents()
    setUndoModal(false)
  }

  // ── Event logging ────────────────────────────────────────────────────────────

  const isET = half === 3 || half === 4
    const minute = isET
      ? 'ET-' + (half === 3 ? '1st' : '2nd')
      : Math.floor(secondsRef.current / 60) + (half === 2 ? 30 : 0)
  const clockDisplay = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
  const totalHome = score.hg * 3 + score.hp
  const totalAway = score.ag * 3 + score.ap
  const diff = Math.abs(totalHome - totalAway)
  const leadText = totalHome > totalAway
    ? `Lead by ${diff} pt${diff !== 1 ? 's' : ''}`
    : totalAway > totalHome
    ? `${fixture.opponent} lead by ${diff} pt${diff !== 1 ? 's' : ''}`
    : 'Level'

  function handleScorePress(type) {
    if (scoringLocked) return
    if (!running && isTracker) {
      const start = window.confirm('The clock hasn\'t started. Start the clock now?')
      if (start) toggleClock()
    }
    setScoringLocked(true)
    setPendingScoreType(type)
    setModal('pickSource')
  }
  function handleOppScorePress(type) {
    if (!running && isTracker) {
      const start = window.confirm('The clock hasn\'t started. Start the clock now?')
      if (start) toggleClock()
    }
    setPendingScoreType(type)
    setModal('pickOppSource')
  }

  async function logEvent(type, playerId, extraData = {}) {
    let newScore = { ...score }
    if (type === 'goal') newScore.hg++
    if (type === 'point') newScore.hp++
    if (type === 'twopoint') newScore.hp += 2
    if (type === 'opp_goal') newScore.ag++
    if (type === 'opp_point') newScore.ap++
    if (type === 'opp_twopoint') newScore.ap += 2

    const deleteLastMap = {
      'undo_goal': 'goal', 'undo_point': 'point', 'undo_twopoint': 'twopoint',
      'undo_opp_goal': 'opp_goal', 'undo_opp_point': 'opp_point', 'undo_opp_twopoint': 'opp_twopoint',
    }

    if (deleteLastMap[type]) {
      const last = [...events].reverse().find(e => e.event_type === deleteLastMap[type])
      if (!last) { alert('No ' + deleteLastMap[type].replace(/_/g, ' ') + ' to remove.'); return }
      await supabase.from('match_events').delete().eq('id', last.id)
      if (type === 'undo_goal') newScore.hg = Math.max(0, newScore.hg - 1)
      if (type === 'undo_point') newScore.hp = Math.max(0, newScore.hp - 1)
      if (type === 'undo_twopoint') newScore.hp = Math.max(0, newScore.hp - 2)
      if (type === 'undo_opp_goal') newScore.ag = Math.max(0, newScore.ag - 1)
      if (type === 'undo_opp_point') newScore.ap = Math.max(0, newScore.ap - 1)
      if (type === 'undo_opp_twopoint') newScore.ap = Math.max(0, newScore.ap - 2)
      setScore(newScore)
      await supabase.from('fixtures').update({
        home_goals: newScore.hg, home_points: newScore.hp,
        away_goals: newScore.ag, away_points: newScore.ap
      }).eq('id', fixture.id)
      await loadEvents()
      return
    }

    const cardUndoMap = {
      'undo_yellow_card': 'yellow_card', 'undo_red_card': 'red_card', 'undo_black_card': 'black_card'
    }
    if (cardUndoMap[type]) {
      const last = [...events].reverse().find(e => e.event_type === cardUndoMap[type] && e.player_id === playerId)
      if (last) await supabase.from('match_events').delete().eq('id', last.id)
      if (type === 'undo_red_card' && playerId) setRedCardedPlayers(prev => prev.filter(id => id !== playerId))
      if (type === 'undo_black_card' && playerId) setBlackCardPlayers(prev => prev.filter(p => p.id !== playerId))
      await loadEvents()
      setModal(null)
      setSelectedPlayer(null)
      return
    }

    await supabase.from('match_events').insert({
      fixture_id: fixture.id, player_id: playerId || null,
      event_type: type, minute, notes: extraData.notes || null,
      opp_jersey_number: extraData.opp_jersey_number || null,
    })

    if (type === 'yellow_card' && playerId && sinBinUsesYellow && ladiesFootball) {
      setBlackCardPlayers(prev => [...prev, { id: playerId, secondsRemaining: sinBinMins * 60 }])
    }

    if (type === 'black_card' && playerId) {
      setBlackCardPlayers(prev => [...prev, { id: playerId, secondsRemaining: sinBinMins * 60 }])
      // For underage men's football/hurling with temp sub rule, immediately open sub modal
      if (blackCardTempSub && !ladiesFootball) {
        setSubOff(lineup.find(p => p.id === playerId) || null)
        setSubInitiatedBySub(true)
        setSubStep(2)
        setModal('sub')
      }
    }
    if (type === 'red_card' && playerId) setRedCardedPlayers(prev => [...prev, playerId])

    setScore(newScore)
    await supabase.from('fixtures').update({
      home_goals: newScore.hg, home_points: newScore.hp,
      away_goals: newScore.ag, away_points: newScore.ap
    }).eq('id', fixture.id)
    await loadEvents()
    setModal(null)
    setSelectedPlayer(null)
    setSubStep(1)
    setSubOff(null)
    setSubInitiatedBySub(false)
    setPendingScoreType(null)
    setPendingScoreSource(null)
    setScoringLocked(false)
  }

  async function logSub(playerOn, playerOff) {
    const off = playerOff || subOff
    if (!off) return
    if (!isUnlimitedSubs && subsUsed >= maxSubs) { alert('Maximum ' + maxSubs + ' substitutions reached.'); return }

    await supabase.from('match_events').insert([
      { fixture_id: fixture.id, player_id: off.id, event_type: 'sub_off', minute },
      { fixture_id: fixture.id, player_id: playerOn.id, event_type: 'sub_on', minute }
    ])

    const slotToFill = off.assigned_number
    const newLineup = lineup.map(p => {
      if (p.id === off.id) {
        return { ...playerOn, assigned_number: slotToFill, match_number: playerOn.match_number }
      }
      return p
    })

    let newSubs = subs.filter(p => p.id !== playerOn.id)
    newSubs = [...newSubs, { ...off }]

    await saveLineupToDb(newLineup, newSubs)
    setLineup(newLineup)
    setSubs(newSubs)
    setSubstitutedOff(prev => [...prev, { ...off }])
    setSubsUsed(prev => prev + 1)
    await loadEvents()
    setModal(null)
    setSubStep(1)
    setSubOff(null)
    setSubInitiatedBySub(false)
    setSelectedPlayer(null)
  }

  async function logMultiSub() {
    if (multiSubsOff.length === 0 || multiSubsOn.length === 0) return
    if (multiSubsOff.length !== multiSubsOn.length) { alert('Number of players coming off must match players coming on.'); return }
    if (!isUnlimitedSubs && subsUsed + multiSubsOff.length > maxSubs) { alert('Not enough substitutions remaining.'); return }

    // Build all events at once
    const events = []
    multiSubsOff.forEach((off, i) => {
      const on = multiSubsOn[i]
      events.push({ fixture_id: fixture.id, player_id: off.id, event_type: 'sub_off', minute })
      events.push({ fixture_id: fixture.id, player_id: on.id, event_type: 'sub_on', minute })
    })
    await supabase.from('match_events').insert(events)

    // Apply all lineup changes at once
    let newLineup = [...lineup]
    let newSubs = [...subs]
    const newSubstitutedOff = []

    multiSubsOff.forEach((off, i) => {
      const on = multiSubsOn[i]
      const slotToFill = off.assigned_number
      newLineup = newLineup.map(p => {
        if (p.id === off.id) return { ...on, assigned_number: slotToFill, match_number: on.match_number }
        return p
      })
      newSubs = newSubs.filter(p => p.id !== on.id)
      newSubs = [...newSubs, { ...off }]
      newSubstitutedOff.push({ ...off })
    })

    await saveLineupToDb(newLineup, newSubs)
    setLineup(newLineup)
    setSubs(newSubs)
    setSubstitutedOff(prev => [...prev, ...newSubstitutedOff])
    setSubsUsed(prev => prev + multiSubsOff.length)
    await loadEvents()
    setMultiSubMode(false)
    setMultiSubsOff([])
    setMultiSubsOn([])
  }

  async function endMatch() {
      const isDraw = score.hg * 3 + score.hp === score.ag * 3 + score.ap
      const isET = halfRef.current === 3 || halfRef.current === 4

      if (isDraw && !isET) {
        const goET = window.confirm('The match is level. Go to Extra Time?\n\nOK = Extra Time\nCancel = End as a Draw')
        if (goET) {
          clearInterval(intervalRef.current)
          setRunning(false)
          runningRef.current = false
          setSeconds(0)
          secondsRef.current = 0
          setHalf(3)
          halfRef.current = 3
          saveClockToDb(0, 3, false)
          await supabase.from('fixtures').update({ match_phase: 'et1' }).eq('id', fixture.id)
          return
        }
      }

      if (!window.confirm('End the match and save the result?')) return
      const phase = isET ? 'et_ft' : 'ft'
      await supabase.from('fixtures').update({
        status: 'completed', clock_running: false,
        clock_seconds_at_start: secondsRef.current,
        tracked_by: null, tracked_by_name: null,
        match_phase: phase
      }).eq('id', fixture.id)
      clearInterval(intervalRef.current)
      clearInterval(syncIntervalRef.current)
      clearInterval(eventsIntervalRef.current)
      onBack()
    }

  function isInSinBin(id) { return blackCardPlayers.some(p => p.id === id) }
  function isRedCarded(id) { return redCardedPlayers.includes(id) }
  function isUnavailable(id) { return isRedCarded(id) || isInSinBin(id) }

  function handlePlayerTap(player, isSub = false) {
    if (multiSubMode && isTracker) {
      if (isSub) {
        // Selecting a sub to come on
        setMultiSubsOn(prev => {
          if (prev.find(p => p.id === player.id)) return prev.filter(p => p.id !== player.id)
          return [...prev, player]
        })
      } else {
        // Selecting a starter to come off
        setMultiSubsOff(prev => {
          if (prev.find(p => p.id === player.id)) return prev.filter(p => p.id !== player.id)
          return [...prev, player]
        })
      }
      return
    }
    if (swapFirst) {
      if (swapFirst === 'pending') { setSwapFirst(player); return }
      if (swapFirst.id === player.id) { setSwapFirst(null); return }
      swapPositions(swapFirst, player)
      return
    }
    setSelectedPlayer({ ...player, isSub })
    setModal('playerAction')
  }

  function handleTouchStart(e) { touchStartX.current = e.touches[0].clientX }
  function handleTouchEnd(e) {
    if (touchStartX.current === null) return
    const diff = touchStartX.current - e.changedTouches[0].clientX
    if (Math.abs(diff) > 50) {
      const idx = tabs.indexOf(activeTab)
      if (diff > 0 && idx < tabs.length - 1) setActiveTab(tabs[idx + 1])
      else if (diff < 0 && idx > 0) setActiveTab(tabs[idx - 1])
    }
    touchStartX.current = null
  }

  // ── Computed stats ───────────────────────────────────────────────────────────

  const scorerTallies = {}
  events.forEach(e => {
    if (!['goal', 'point', 'twopoint'].includes(e.event_type)) return
    const key = e.player_id || 'unknown'
    const name = e.players?.full_name || 'Unknown'
    if (!scorerTallies[key]) scorerTallies[key] = { name, goals: 0, points: 0, goalsPlay: 0, pointsPlay: 0 }
    if (e.event_type === 'goal') { scorerTallies[key].goals++; if (e.notes === 'from_play') scorerTallies[key].goalsPlay++ }
    if (e.event_type === 'point') { scorerTallies[key].points++; if (e.notes === 'from_play') scorerTallies[key].pointsPlay++ }
    if (e.event_type === 'twopoint') { scorerTallies[key].points += 2; if (e.notes === 'from_play') scorerTallies[key].pointsPlay += 2 }
  })
  const scorerList = Object.values(scorerTallies).sort((a, b) => (b.goals * 3 + b.points) - (a.goals * 3 + a.points))

  const ourGoalsPlay = events.filter(e => e.event_type === 'goal' && e.notes === 'from_play').length
  const ourPointsPlay = events.filter(e => e.event_type === 'point' && e.notes === 'from_play').length +
    events.filter(e => e.event_type === 'twopoint' && e.notes === 'from_play').length * 2
  const oppGoalsPlay = events.filter(e => e.event_type === 'opp_goal' && e.notes === 'from_play').length
  const oppPointsPlay = events.filter(e => e.event_type === 'opp_point' && e.notes === 'from_play').length +
    events.filter(e => e.event_type === 'opp_twopoint' && e.notes === 'from_play').length * 2

  const ourWides = events.filter(e => e.event_type === 'wide').length
  const oppWides = events.filter(e => e.event_type === 'opp_wide').length
  const ourFortyFives = events.filter(e => e.event_type === 'fortyfive_won').length
  const oppFortyFives = events.filter(e => e.event_type === 'fortyfive_conceded').length
  const ourKOWon = events.filter(e => e.event_type === 'kickout_won').length
  const ourKOLost = events.filter(e => e.event_type === 'kickout_lost').length
  const oppKOWon = events.filter(e => e.event_type === 'opp_kickout_won').length
  const oppKOLost = events.filter(e => e.event_type === 'opp_kickout_lost').length
  const freesWon = events.filter(e => e.event_type === 'free_won').length
  const freesConceded = events.filter(e => e.event_type === 'free_conceded').length

  const allOnPitch = lineup
  const allPlayers = [...lineup, ...subs]
  function playerNum(p) { return Number(p.match_number || p.jersey_number || p.assigned_number) || 0 }
  function sortDesc(list) { return [...list].sort((a, b) => playerNum(b) - playerNum(a)) }
  function sortAsc(list) { return [...list].sort((a, b) => playerNum(a) - playerNum(b)) }
  const availableSubs = subs.filter(p => {
    if (isUnlimitedSubs || isFriendly) return true
    return !substitutedOff.find(x => x.id === p.id)
  })

  const oppLogo = getClubLogo(fixture.opponent)

  if (lineup.length === 0 && players.length > 0) {
    return <LineupPicker players={players} fixture={fixture} onSave={saveLineup} onBack={onBack} previousSelection={previousSelection} />
  }

  const sourceOptions = (type) => {
    const opts = [
      { value: 'from_play', label: 'From Play' },
      { value: 'free', label: 'Free' },
      { value: 'sideline', label: 'Sideline' },
    ]
    if (type !== 'wide' && type !== 'opp_wide' && type !== 'twopoint' && type !== 'opp_twopoint') {
      opts.push({ value: isHurl ? '65' : '45', label: setPieceLabel })
      opts.push({ value: 'penalty', label: 'Penalty' })
    }
    return opts
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 70px)', overflow: 'hidden' }}>

      {/* Score bar */}
      <div style={{ background: '#185FA5', color: 'white', padding: '10px 12px', flexShrink: 0 }}>
        <div style={{ textAlign: 'center', fontSize: '11px', opacity: 0.75, marginBottom: '4px' }}>
          {isHurl ? '🥍 ' + (sport === 'camogie' ? 'Camogie' : 'Hurling') : '⚽ Football'} · Team {fixture.team_number || 1}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '3px' }}>
            <img src={myClubLogo || DEFAULT_LOGO} alt="" style={{ width: '26px', height: '26px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none' }} />
              <p style={{ fontSize: '11px', opacity: 0.9, fontWeight: '500' }}>{fixture.squads?.name || 'Us'}</p>
            </div>
            <h3 style={{ fontSize: '34px', fontWeight: '600', letterSpacing: '-1px', lineHeight: 1 }}>
              {score.hg}-{String(score.hp).padStart(2, '0')}
            </h3>
            <p style={{ fontSize: '11px', opacity: 0.7 }}>({totalHome} pts)</p>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '11px', opacity: 0.7, marginBottom: '2px' }}>
              {matchPhase === 'ft' ? '🏁 Full Time' :
               matchPhase === 'et_ft' ? '🏁 ET Full Time' :
               matchPhase === 'ht' ? '⏸ Half Time' :
               matchPhase === 'et_ht' ? '⏸ ET Half Time' :
               half === 1 ? '1st Half' : half === 2 ? '2nd Half' :
               half === 3 ? 'ET 1st Half' : 'ET 2nd Half'}
            </p>
            <h3 style={{ fontSize: '22px', fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>{clockDisplay}</h3>
            <div style={{ marginTop: '4px' }}>
              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                <button onClick={toggleClock} style={{ ...clockBtn, flex: 1, opacity: isTracker ? 1 : 0.5 }}>{running ? 'Pause' : 'Start'}</button>
                {(half === 1 || half === 3) && <button onClick={isTracker ? resetForSecondHalf : null} style={{ ...clockBtn, flex: 1, opacity: isTracker ? 1 : 0.5 }}>{half === 3 ? 'ET HT' : 'HT'}</button>}
                <button onClick={isTracker ? () => setResetModal(true) : null} style={{ ...clockBtn, flex: 1, opacity: isTracker ? 1 : 0.5 }}>Reset</button>
                <button onClick={isTracker ? endMatch : null} style={{ ...clockBtn, flex: 1, background: 'rgba(255,80,80,0.35)', opacity: isTracker ? 1 : 0.5 }}>End</button>
              </div>
              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                {!running && seconds === 0 && isTracker && <button onClick={handleEditLineup} style={{ ...clockBtn, flex: 1, background: 'rgba(255,200,0,0.3)' }}>✏️ Edit Lineup</button>}
                <button onClick={onBack} style={{ ...clockBtn, flex: 1 }}>Back to Fixtures</button>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '3px' }}>
              <img src={oppLogo} alt="" style={{ width: '26px', height: '26px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none' }} />
              <p style={{ fontSize: '11px', opacity: 0.9, fontWeight: '500' }}>{fixture.opponent}</p>
            </div>
            <h3 style={{ fontSize: '34px', fontWeight: '600', letterSpacing: '-1px', lineHeight: 1 }}>
              {score.ag}-{String(score.ap).padStart(2, '00')}
            </h3>
            <p style={{ fontSize: '11px', opacity: 0.7 }}>({totalAway} pts)</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '5px', fontSize: '11px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', padding: '3px 12px' }}>
          {leadText}
        </div>
        {!isUnlimitedSubs && (
          <div style={{ textAlign: 'center', marginTop: '2px', fontSize: '10px', opacity: 0.7 }}>
            Subs: {subsUsed}/{maxSubs}
          </div>
        )}
        {/* Tracking badge */}
        {trackedBy && (
          <div style={{ textAlign: 'center', marginTop: '4px', fontSize: '10px', background: isTracker ? 'rgba(59,109,17,0.4)' : 'rgba(255,255,255,0.15)', borderRadius: '20px', padding: '3px 12px' }}>
            {isTracker ? '🟢 You are tracking this match' : '👁 Tracked by ' + trackedByName}
          </div>
        )}
      </div>

      {/* Sin bin */}
        {(blackCardPlayers.length > 0 || oppBlackCardPlayers.length > 0) && (
        <div style={{ background: '#2C2C2A', color: 'white', padding: '6px 14px', flexShrink: 0 }}>
          <p style={{ fontSize: '10px', opacity: 0.7, marginBottom: '3px' }}>⬛ SIN BIN</p>
          {blackCardPlayers.map(p => {
            const player = allPlayers.find(x => x.id === p.id)
            const mins = Math.floor((p.secondsRemaining ?? 0) / 60)
            const secs = (p.secondsRemaining ?? 0) % 60
            return (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                <span>Us · {player?.full_name || 'Player'}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: (p.secondsRemaining ?? 0) < 60 ? '#ff6b6b' : 'white', fontWeight: '500' }}>
                  {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
                </span>
              </div>
            )
          })}
          {oppBlackCardPlayers.map(p => {
            const mins = Math.floor((p.secondsRemaining ?? 0) / 60)
            const secs = (p.secondsRemaining ?? 0) % 60
            return (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                <span>Opp #{p.num}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: (p.secondsRemaining ?? 0) < 60 ? '#ff6b6b' : 'white', fontWeight: '500' }}>
                  {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '0.5px solid #e0e0e0', background: 'white', flexShrink: 0 }}>
        {[
          { id: 'score', icon: '⚽', label: 'Score' },
          { id: 'pitch', icon: '🏟️', label: 'Pitch' },
          { id: 'squad', icon: '👥', label: 'Squad' },
          { id: 'events', icon: '📋', label: 'Events' },
          { id: 'opposition', icon: '👥', label: 'Opp' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ flex: 1, padding: '9px 2px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: activeTab === t.id ? '#185FA5' : '#888', borderBottom: activeTab === t.id ? '2px solid #185FA5' : '2px solid transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <span style={{ fontSize: '17px' }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>

        {/* SCORE TAB */}
        {activeTab === 'score' && (
          <ScoreTab
            fixture={fixture}
            isTracker={isTracker}
            trackedByName={trackedByName}
            isHurl={isHurl}
            allowsTwoPointFootball={allowsTwoPointFootball}
            setPieceLabel={setPieceLabel}
            koLabelPlural={koLabelPlural}
            score={score}
            scorerList={scorerList}
            ourGoalsPlay={ourGoalsPlay}
            ourPointsPlay={ourPointsPlay}
            oppGoalsPlay={oppGoalsPlay}
            oppPointsPlay={oppPointsPlay}
            ourWides={ourWides}
            oppWides={oppWides}
            ourFortyFives={ourFortyFives}
            oppFortyFives={oppFortyFives}
            ourKOWon={ourKOWon}
            ourKOLost={ourKOLost}
            oppKOWon={oppKOWon}
            oppKOLost={oppKOLost}
            freesWon={freesWon}
            freesConceded={freesConceded}
            handleScorePress={handleScorePress}
            handleOppScorePress={handleOppScorePress}
            logEvent={logEvent}
            undoLast={undoLast}
            setModal={setModal}
            setUndoModal={setUndoModal}
            setMultiSubMode={setMultiSubMode}
            setActiveTab={setActiveTab}
            setSubStep={setSubStep}
            setSubOff={setSubOff}
            setSubInitiatedBySub={setSubInitiatedBySub}
          />
        )}

        {/* PITCH TAB */}
        {activeTab === 'pitch' && (
          <PitchTab
            fixture={fixture}
            lineup={lineup}
            subs={subs}
            substitutedOff={substitutedOff}
            multiSubMode={multiSubMode}
            multiSubsOff={multiSubsOff}
            multiSubsOn={multiSubsOn}
            swapFirst={swapFirst}
            isUnlimitedSubs={isUnlimitedSubs}
            isFriendly={isFriendly}
            isTracker={isTracker}
            redCardedPlayers={redCardedPlayers}
            blackCardPlayers={blackCardPlayers}
            isRedCarded={isRedCarded}
            isInSinBin={isInSinBin}
            handlePlayerTap={handlePlayerTap}
            logMultiSub={logMultiSub}
            setMultiSubMode={setMultiSubMode}
            setMultiSubsOff={setMultiSubsOff}
            setMultiSubsOn={setMultiSubsOn}
            setSwapFirst={setSwapFirst}
          />
        )}

      {/* SQUAD TAB */}
        {activeTab === 'squad' && (
          <SquadTab
            lineup={lineup}
            subs={subs}
            substitutedOff={substitutedOff}
            scorerTallies={scorerTallies}
            fixture={fixture}
            isUnlimitedSubs={isUnlimitedSubs}
            isFriendly={isFriendly}
            isRedCarded={isRedCarded}
            isInSinBin={isInSinBin}
            handlePlayerTap={handlePlayerTap}
          />
        )}

        {/* OPPOSITION TAB */}
        {activeTab === 'opposition' && (
          <OppositionTab
            fixture={fixture}
            events={events}
            isTracker={isTracker}
          />
        )}

        {/* EVENTS TAB */}
        {activeTab === 'events' && (
          <div style={{ padding: '10px 14px' }}>
            <EventTable
              events={events} fixture={fixture} sport={sport}
              ourWides={ourWides} oppWides={oppWides}
              ourFortyFives={ourFortyFives} oppFortyFives={oppFortyFives}
              ourKOWon={ourKOWon} ourKOLost={ourKOLost}
              oppKOWon={oppKOWon} oppKOLost={oppKOLost}
              freesWon={freesWon} freesConceded={freesConceded}
            />
          </div>
        )}
      </div>


      {/* ── Modals ── */}

      {resetModal && (
        <div style={overlayStyle} onClick={() => setResetModal(false)}>
          <div style={modalBoxStyle} onClick={e => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ fontSize: '15px', fontWeight: '500' }}>Reset Options</h3>
              <button onClick={() => setResetModal(false)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <p style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>What would you like to reset?</p>
              <button style={resetBtnStyle('#185FA5')} onClick={() => { if (window.confirm('Reset the match clock? This cannot be undone.')) handleReset('time') }}>⏱ Reset Clock Only</button>
              <button style={resetBtnStyle('#3B6D11')} onClick={() => { if (window.confirm('Reset the scores to 0-0? This cannot be undone.')) handleReset('scores') }}>🏆 Reset Scores Only</button>
              <button style={resetBtnStyle('#533AB7')} onClick={() => { if (window.confirm('Reset the squad selection for this match? This cannot be undone.')) handleReset('squad') }}>👥 Reset Squad Selection</button>
              <button style={resetBtnStyle('#854F0B')} onClick={() => { if (window.confirm('Clear the entire event log for this match? This cannot be undone.')) handleReset('events') }}>📋 Clear Event Log</button>
              <button style={resetBtnStyle('#A32D2D')} onClick={() => { if (window.confirm('Reset ALL match data? This cannot be undone.')) handleReset('all') }}>🗑️ Reset All Match Data</button>
            </div>
          </div>
        </div>
      )}

      {undoModal && (
        <div style={overlayStyle} onClick={() => setUndoModal(false)}>
          <div style={modalBoxStyle} onClick={e => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ fontSize: '15px', fontWeight: '500' }}>↩ Undo Last Event</h3>
              <button onClick={() => setUndoModal(false)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <p style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Removes the most recent event of that type.</p>
              <button style={resetBtnStyle('#854F0B')} onClick={() => undoLast('wide_us')}>↩ Undo Last Our Wide / {setPieceLabel}</button>
              <button style={resetBtnStyle('#888')} onClick={() => undoLast('wide_opp')}>↩ Undo Last Opp Wide / {setPieceLabel}</button>
              <button style={resetBtnStyle('#3B6D11')} onClick={() => undoLast('kickout')}>↩ Undo Last Kick Out</button>
              <button style={resetBtnStyle('#0F6E56')} onClick={() => undoLast('free')}>↩ Undo Last Free</button>
              <button style={resetBtnStyle('#533AB7')} onClick={() => undoLast('sub')}>↩ Undo Last Substitution (reverses lineup)</button>
              <button style={resetBtnStyle('#854F0B')} onClick={() => undoLast('card')}>↩ Undo Last Card</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'ourWide' && (
        <Modal title="Our Wide — what happened?" onClose={() => setModal(null)}>
          <div style={{ padding: '14px 16px' }}>
            <button style={{ ...modalActionBtn('#3B6D11'), width: '100%', marginBottom: '10px', padding: '16px', fontSize: '14px' }}
              onClick={() => { logEvent('fortyfive_won', null); setModal(null) }}>
              🎯 {setPieceLabel} Won — they defended, ball went wide
            </button>
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px', textAlign: 'center' }}>or select which player hit it wide</p>
            {sortDesc(allOnPitch.filter(p => !isUnavailable(p.id))).map(p => (
              <PlayerRow key={p.id} player={p} onTap={() => { logEvent('wide', p.id, { notes: 'from_play' }); setModal(null) }} />
            ))}
            <div onClick={() => { logEvent('wide', null, { notes: 'from_play' }); setModal(null) }}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#888' }}>?</div>
              <span style={{ fontSize: '15px', color: '#888', fontStyle: 'italic' }}>Unknown player</span>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'oppWide' && (
        <Modal title="Opp Wide — what happened?" onClose={() => setModal(null)}>
          <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <button style={{ ...modalActionBtn('#A32D2D'), padding: '16px', fontSize: '13px' }}
              onClick={() => { logEvent('opp_wide', null); setModal(null) }}>
              ↗ Opp hit it wide — our kick out
            </button>
            <button style={{ ...modalActionBtn('#854F0B'), padding: '16px', fontSize: '13px' }}
              onClick={() => { logEvent('fortyfive_conceded', null); setModal(null) }}>
              🎯 {setPieceLabel} Conceded — we defended, ball wide
            </button>
          </div>
        </Modal>
      )}

      {modal === 'pickSource' && pendingScoreType && (
        <Modal
          title={(pendingScoreType === 'goal' ? 'Goal' : pendingScoreType === 'twopoint' ? '2-Point' : 'Point') + ' — scored from?'}
          onClose={() => { setModal(null); setPendingScoreType(null); setPendingScoreSource(null); setScoringLocked(false) }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '14px 16px' }}>
            {sourceOptions(pendingScoreType).map(opt => (
              <button key={opt.value}
                style={{ ...modalActionBtn('#185FA5'), padding: '16px', fontSize: '14px' }}
                onClick={() => { setPendingScoreSource(opt.value); setModal('pickScorer') }}>
                {opt.label}
              </button>
            ))}
          </div>
        </Modal>
      )}

{modal === 'pickOppSource' && pendingScoreType && (
        <Modal
          title={fixture.opponent + ' — scored from?'}
          onClose={() => { setModal(null); setPendingScoreType(null); setPendingScoreSource(null); setPendingOppJersey(null) }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '14px 16px' }}>
            {sourceOptions(pendingScoreType).map(opt => (
              <button key={opt.value}
                style={{ ...modalActionBtn('#A32D2D'), padding: '16px', fontSize: '14px' }}
                onClick={() => { setPendingScoreSource(opt.value); setModal('pickOppJersey') }}>
                {opt.label}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {modal === 'pickOppJersey' && pendingScoreType && pendingScoreSource && (
        <Modal
          title={'Who scored? (jersey number)'}
          onClose={() => { setModal(null); setPendingScoreType(null); setPendingScoreSource(null); setPendingOppJersey(null) }}>
          <div style={{ padding: '12px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '12px' }}>
              {Array.from({ length: 26 }, (_, i) => i + 1).map(num => (
                <button key={num}
                  onClick={() => { logEvent(pendingScoreType, null, { notes: pendingScoreSource, opp_jersey_number: num }); setModal(null); setPendingScoreType(null); setPendingScoreSource(null); setPendingOppJersey(null) }}
                  style={{ padding: '12px 4px', borderRadius: '8px', border: '1px solid #e0e0e0', background: '#f8f8f8', cursor: 'pointer', fontSize: '15px', fontWeight: '600', color: '#333' }}>
                  {num}
                </button>
              ))}
            </div>
            <button
              onClick={() => { logEvent(pendingScoreType, null, { notes: pendingScoreSource, opp_jersey_number: null }); setModal(null); setPendingScoreType(null); setPendingScoreSource(null); setPendingOppJersey(null) }}
              style={{ width: '100%', padding: '13px', borderRadius: '10px', border: '1px solid #ddd', background: '#f0f0f0', cursor: 'pointer', fontSize: '14px', color: '#888' }}>
              Unknown player
            </button>
          </div>
        </Modal>
      )}

      {modal === 'pickScorer' && pendingScoreType && pendingScoreSource && (
        <Modal
          title={'Who scored the ' + (pendingScoreType === 'goal' ? 'goal' : pendingScoreType === 'twopoint' ? '2-pointer' : 'point') + '? (' + getSourceLabel(pendingScoreSource, sport) + ')'}
          onClose={() => { setModal(null); setPendingScoreType(null); setPendingScoreSource(null); setScoringLocked(false) }}>
          {sortDesc(allOnPitch.filter(p => !isUnavailable(p.id))).map(p => (
            <PlayerRow key={p.id} player={p} onTap={() => logEvent(pendingScoreType, p.id, { notes: pendingScoreSource })} />
          ))}
          <div onClick={() => logEvent(pendingScoreType, null, { notes: pendingScoreSource })}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#888' }}>?</div>
            <span style={{ fontSize: '15px', color: '#888', fontStyle: 'italic' }}>Unknown scorer</span>
          </div>
        </Modal>
      )}

      {modal === 'ourKickout' && (
        <Modal title={'Our ' + koLabel} onClose={() => setModal(null)}>
          <div style={{ padding: '12px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button style={{ ...modalActionBtn('#3B6D11'), padding: '16px', fontSize: '15px' }} onClick={() => setModal('ourKickoutWon')}>Won ▾</button>
              <button style={{ ...modalActionBtn('#A32D2D'), padding: '16px', fontSize: '15px' }} onClick={() => logEvent('kickout_lost', null)}>Lost</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'ourKickoutWon' && (
        <Modal title={'Who won the ' + koLabel.toLowerCase() + '?'} onClose={() => setModal(null)}>
          {sortAsc(allOnPitch.filter(p => !isUnavailable(p.id))).map(p => (
            <PlayerRow key={p.id} player={p} onTap={() => logEvent('kickout_won', p.id)} />
          ))}
          <div onClick={() => logEvent('kickout_won', null)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#888' }}>?</div>
            <span style={{ fontSize: '15px', color: '#888', fontStyle: 'italic' }}>Unknown player</span>
          </div>
        </Modal>
      )}

      {modal === 'oppKickout' && (
        <Modal title={'Opp ' + koLabel} onClose={() => setModal(null)}>
          <div style={{ padding: '12px 16px' }}>
            <p style={{ fontSize: '14px', color: '#888', marginBottom: '12px' }}>Result of opposition {koLabel.toLowerCase()}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button style={{ ...modalActionBtn('#A32D2D'), padding: '16px', fontSize: '15px' }} onClick={() => logEvent('opp_kickout_won', null)}>Opp Won</button>
              <button style={{ ...modalActionBtn('#3B6D11'), padding: '16px', fontSize: '15px' }} onClick={() => setModal('oppKickoutWeWon')}>We Won It ▾</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'oppKickoutWeWon' && (
        <Modal title={'Who won the opp ' + koLabel.toLowerCase() + '?'} onClose={() => setModal(null)}>
          {sortAsc(allOnPitch.filter(p => !isUnavailable(p.id))).map(p => (
            <PlayerRow key={p.id} player={p} onTap={() => logEvent('opp_kickout_lost', p.id)} />
          ))}
          <div onClick={() => logEvent('opp_kickout_lost', null)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#888' }}>?</div>
            <span style={{ fontSize: '15px', color: '#888', fontStyle: 'italic' }}>Unknown player</span>
          </div>
        </Modal>
      )}

      {modal === 'freeWon' && (
        <Modal title="Who won the free?" onClose={() => setModal(null)}>
          {sortDesc(allOnPitch.filter(p => !isUnavailable(p.id))).map(p => (
            <PlayerRow key={p.id} player={p} onTap={() => logEvent('free_won', p.id)} />
          ))}
          <div onClick={() => logEvent('free_won', null, { notes: 'opp_fouled_ball' })}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer', borderTop: '0.5px solid #f0f0f0' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#A32D2D' }}>⚠️</div>
            <span style={{ fontSize: '15px', color: '#A32D2D' }}>Opposition fouled the ball</span>
          </div>
          <div onClick={() => logEvent('free_won', null)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#888' }}>?</div>
            <span style={{ fontSize: '15px', color: '#888', fontStyle: 'italic' }}>Unknown player</span>
          </div>
        </Modal>
      )}

      {modal === 'freeConceded' && (
        <Modal title="Foul conceded by" onClose={() => setModal(null)}>
          <div style={{ padding: '12px 16px 4px' }}>
            <p style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>Select our player or log as unknown</p>
          </div>
          {sortAsc(allOnPitch.filter(p => !isUnavailable(p.id))).map(p => (
            <PlayerRow key={p.id} player={p} onTap={() => logEvent('free_conceded', p.id)} />
          ))}
          <div onClick={() => logEvent('free_conceded', null)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', cursor: 'pointer', borderTop: '0.5px solid #f0f0f0' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#888' }}>?</div>
            <span style={{ fontSize: '15px', color: '#888', fontStyle: 'italic' }}>Unknown / opposition foul</span>
          </div>
        </Modal>
      )}

      {modal === 'playerAction' && selectedPlayer && !selectedPlayer.isSub && (
        <Modal title={selectedPlayer.full_name} onClose={() => { setModal(null); setSelectedPlayer(null) }}>
          {isUnavailable(selectedPlayer.id) ? (
            <div style={{ padding: '16px' }}>
              {isRedCarded(selectedPlayer.id) && (
                <>
                  <div style={{ background: '#FCEBEB', borderRadius: '8px', padding: '12px', marginBottom: '12px', color: '#A32D2D', fontSize: '13px' }}>🟥 Sent off.</div>
                  <button style={modalActionBtn('#A32D2D')} onClick={() => logEvent('undo_red_card', selectedPlayer.id)}>↩ Undo Red Card</button>
                </>
              )}
              {isInSinBin(selectedPlayer.id) && (
                <>
                  <div style={{ background: '#f0efea', borderRadius: '8px', padding: '12px', marginBottom: '12px', color: '#444', fontSize: '13px' }}>⬛ In sin bin.</div>
                  <button style={modalActionBtn('#333')} onClick={() => logEvent('undo_black_card', selectedPlayer.id)}>↩ Undo Black Card</button>
                </>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '14px 16px' }}>
              <button style={{ ...modalActionBtn('#185FA5'), padding: '14px', fontSize: '14px' }} onClick={() => { setPendingScoreType('goal'); setPendingScoreSource(null); setScoringLocked(true); setModal('pickSource') }}>⚽ Goal</button>
              <button style={{ ...modalActionBtn('#3B6D11'), padding: '14px', fontSize: '14px' }} onClick={() => { setPendingScoreType('point'); setPendingScoreSource(null); setScoringLocked(true); setModal('pickSource') }}>🎯 Point</button>
            {(!isHurl && allowsTwoPointFootball) && <button style={{ ...modalActionBtn('#0F6E56'), padding: '14px', fontSize: '14px' }} onClick={() => { setPendingScoreType('twopoint'); setPendingScoreSource(null); setScoringLocked(true); setModal('pickSource') }}>2️⃣ 2-Point</button>}
              <button style={{ ...modalActionBtn('#854F0B'), padding: '14px', fontSize: '14px' }} onClick={() => setModal('cardForPlayer')}>🟨 Card</button>
              <button style={{ ...modalActionBtn('#3B6D11'), padding: '14px', fontSize: '14px' }} onClick={() => setModal('ourKickoutWon')}>🥅 {koLabel} Won</button>
              <button style={{ ...modalActionBtn('#0F6E56'), padding: '14px', fontSize: '14px' }} onClick={() => logEvent('free_won', selectedPlayer.id)}>🚩 Free Won</button>
              <button style={{ ...modalActionBtn('#854F0B'), padding: '14px', fontSize: '14px' }} onClick={() => setModal('ourWide')}>↗ Wide</button>
              {availableSubs.length > 0 && (isUnlimitedSubs || subsUsed < maxSubs) && (
                <button style={{ ...modalActionBtn('#533AB7'), padding: '14px', fontSize: '14px' }} onClick={() => { setSubOff(selectedPlayer); setSubInitiatedBySub(false); setSubStep(2); setModal('sub') }}>🔄 Sub Off</button>
              )}
            </div>
          )}
        </Modal>
      )}

      {modal === 'playerAction' && selectedPlayer && selectedPlayer.isSub && (
        <Modal title={selectedPlayer.full_name + ' (Sub)'} onClose={() => { setModal(null); setSelectedPlayer(null) }}>
          {isRedCarded(selectedPlayer.id) ? (
            <div style={{ padding: '16px' }}>
              <div style={{ background: '#FCEBEB', borderRadius: '8px', padding: '12px', marginBottom: '12px', color: '#A32D2D', fontSize: '13px' }}>🟥 Red card received.</div>
              <button style={modalActionBtn('#A32D2D')} onClick={() => logEvent('undo_red_card', selectedPlayer.id)}>↩ Undo Red Card</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '14px 16px' }}>
              <button style={{ ...modalActionBtn('#185FA5'), padding: '14px', fontSize: '14px' }} onClick={() => { setPendingScoreType('goal'); setPendingScoreSource(null); setScoringLocked(true); setModal('pickSource') }}>⚽ Goal</button>
              <button style={{ ...modalActionBtn('#3B6D11'), padding: '14px', fontSize: '14px' }} onClick={() => { setPendingScoreType('point'); setPendingScoreSource(null); setScoringLocked(true); setModal('pickSource') }}>🎯 Point</button>
            {(!isHurl && allowsTwoPointFootball) && <button style={{ ...modalActionBtn('#0F6E56'), padding: '14px', fontSize: '14px' }} onClick={() => { setPendingScoreType('twopoint'); setPendingScoreSource(null); setScoringLocked(true); setModal('pickSource') }}>2️⃣ 2-Point</button>}
              <button style={{ ...modalActionBtn('#854F0B'), padding: '14px', fontSize: '14px' }} onClick={() => setModal('cardForPlayer')}>🟨 Card</button>
              <button style={{ ...modalActionBtn('#3B6D11'), padding: '14px', fontSize: '14px' }} onClick={() => logEvent('kickout_won', selectedPlayer.id)}>🥅 {koLabel} Won</button>
              <button style={{ ...modalActionBtn('#0F6E56'), padding: '14px', fontSize: '14px' }} onClick={() => logEvent('free_won', selectedPlayer.id)}>🚩 Free Won</button>
              <button style={{ ...modalActionBtn('#854F0B'), padding: '14px', fontSize: '14px' }} onClick={() => setModal('ourWide')}>↗ Wide</button>
              {(isUnlimitedSubs || subsUsed < maxSubs) && (
                <button style={{ ...modalActionBtn('#533AB7'), padding: '14px', fontSize: '14px' }} onClick={() => { setSubOff(selectedPlayer); setSubInitiatedBySub(true); setSubStep(2); setModal('sub') }}>🔄 Sub On</button>
              )}
            </div>
          )}
        </Modal>
      )}

      {modal === 'cardForPlayer' && selectedPlayer && (
        <Modal title={'Card for ' + selectedPlayer.full_name} onClose={() => setModal('playerAction')}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', padding: '14px 16px' }}>
            <button style={{ ...modalActionBtn('#854F0B'), padding: '14px' }} onClick={() => logEvent('yellow_card', selectedPlayer.id)}>🟨 Yellow</button>
            <button style={{ ...modalActionBtn('#A32D2D'), padding: '14px' }} onClick={() => logEvent('red_card', selectedPlayer.id)}>🟥 Red</button>
            <button style={{ ...modalActionBtn('#333'), padding: '14px' }} onClick={() => logEvent('black_card', selectedPlayer.id)}>⬛ Black</button>
          </div>
          <div style={{ padding: '0 16px 14px' }}>
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>Undo a card</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <button style={{ ...modalActionBtn('#854F0B'), fontSize: '12px', padding: '10px' }} onClick={() => logEvent('undo_yellow_card', selectedPlayer.id)}>↩ Yellow</button>
              <button style={{ ...modalActionBtn('#A32D2D'), fontSize: '12px', padding: '10px' }} onClick={() => logEvent('undo_red_card', selectedPlayer.id)}>↩ Red</button>
              <button style={{ ...modalActionBtn('#333'), fontSize: '12px', padding: '10px' }} onClick={() => logEvent('undo_black_card', selectedPlayer.id)}>↩ Black</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'card' && (
        <Modal title="Select card type" onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', padding: '14px 16px' }}>
            <button style={{ ...modalActionBtn('#854F0B'), padding: '14px' }} onClick={() => setModal('yellow')}>🟨 Yellow</button>
            <button style={{ ...modalActionBtn('#A32D2D'), padding: '14px' }} onClick={() => setModal('red')}>🟥 Red</button>
            <button style={{ ...modalActionBtn('#333'), padding: '14px' }} onClick={() => setModal('black')}>⬛ Black</button>
          </div>
        </Modal>
      )}

      {(modal === 'yellow' || modal === 'red' || modal === 'black') && (
        <Modal title="Select player" onClose={() => setModal('card')}>
          {sortAsc(allPlayers).map(p => <PlayerRow key={p.id} player={p} onTap={() => logEvent(modal + '_card', p.id)} />)}
        </Modal>
      )}

      {modal === 'opp_card' && (
        <OppCardModal
          onLog={(num, type) => {
            logEvent('opp_card', null, { notes: type + ' card · ' + (num ? 'Opp #' + num : 'unknown'), opp_jersey_number: num || null })
            if (type === 'black') setOppBlackCardPlayers(prev => [...prev, { id: 'opp_' + Date.now(), num: num || '?', secondsRemaining: 600 }])
          }}
          onClose={() => setModal(null)}
        />
      )}

      {modal === 'sub' && (
        <Modal
          title={subStep === 1 ? 'Player coming OFF' : subInitiatedBySub ? 'Who is ' + subOff?.full_name + ' replacing?' : 'Who is coming ON for ' + subOff?.full_name + '?'}
          onClose={() => { setModal(null); setSubStep(1); setSubOff(null); setSubInitiatedBySub(false) }}>
          {subStep === 1 && sortAsc(allOnPitch.filter(p => !isRedCarded(p.id))).map(p => (
            <PlayerRow key={p.id} player={p} onTap={() => { setSubOff(p); setSubInitiatedBySub(false); setSubStep(2) }} label="Off" labelColor="#A32D2D" />
          ))}
          {subStep === 2 && !subInitiatedBySub && sortAsc(availableSubs.filter(p => !isRedCarded(p.id))).map(p => (
            <PlayerRow key={p.id} player={p} onTap={() => logSub(p, subOff)} label="On" labelColor="#3B6D11" />
          ))}
          {subStep === 2 && subInitiatedBySub && sortAsc(allOnPitch.filter(p => !isRedCarded(p.id))).map(p => (
            <PlayerRow key={p.id} player={p} onTap={() => logSub(subOff, p)} label="Off" labelColor="#A32D2D" />
          ))}
        </Modal>
      )}
    </div>
  )
}


function OppCardModal({ onLog, onClose }) {
  const [num, setNum] = useState('')
  const [type, setType] = useState('yellow')
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalBoxStyle} onClick={e => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ fontSize: '15px', fontWeight: '500' }}>Opposition Card</h3>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>
        <div style={{ padding: '12px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            {['yellow','red','black'].map(t => (
              <button key={t} onClick={() => setType(t)}
                style={{ padding: '12px', borderRadius: '8px', border: '2px solid ' + (type===t?'#185FA5':'#e0e0e0'), background: type===t?'#E6F1FB':'white', cursor: 'pointer', fontSize: '13px' }}>
                {t==='yellow'?'🟨':t==='red'?'🟥':'⬛'} {t.charAt(0).toUpperCase()+t.slice(1)}
              </button>
            ))}
          </div>
          <select style={{ display: 'block', width: '100%', padding: '12px', marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', background: 'white' }}
            value={num} onChange={e => setNum(e.target.value)}>
            <option value="">Unknown player</option>
            {Array.from({length:25},(_,i)=><option key={i+1} value={i+1}>#{i+1}</option>)}
          </select>
          <button style={{ display: 'block', width: '100%', padding: '14px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', cursor: 'pointer' }}
            onClick={() => onLog(num, type)}>Log Card</button>
        </div>
      </div>
    </div>
  )
}

function LineupPicker({ players, fixture, onSave, onBack, previousSelection = [] }) {
  const draftKey = 'gaa_lineup_draft_' + fixture.id

  const [slots, setSlotsState] = useState(() => {

    // Restore an in-progress draft first, if one exists for this fixture
    try {
      const saved = localStorage.getItem(draftKey)
      if (saved) {
        const parsed = JSON.parse(saved)
        if (parsed && Array.isArray(parsed.starters) && Array.isArray(parsed.subs)) {
          return parsed
        }
      }
    } catch (e) { /* ignore corrupt/missing draft */ }

    const startersCount = fixture.players_per_side || 15
    const arr = Array(startersCount).fill(null)
    const subsArr = []
    if (previousSelection.length > 0) {
      previousSelection.forEach((p, i) => {
        if (i < startersCount) {
          const num = p.assigned_number ? parseInt(p.assigned_number) : i + 1
          const slotIdx = num >= 1 && num <= startersCount ? num - 1 : i
          arr[slotIdx] = { ...p }
        } else { subsArr.push({ ...p }) }
      })
    }
    return { starters: arr, subs: subsArr }
  })

  function setSlots(newSlots) {
    setSlotsState(newSlots)
    try { localStorage.setItem(draftKey, JSON.stringify(newSlots)) } catch (e) { /* ignore storage errors */ }
  }

  useEffect(() => {
    if (fixture.players_per_side) return
    if (slots.starters.some(Boolean) || slots.subs.length > 0) return
    supabase.from('fixtures')
      .select('players_per_side')
      .eq('squad_id', fixture.squad_id)
      .eq('sport', fixture.sport)
      .eq('team_number', fixture.team_number || 1)
      .not('players_per_side', 'is', null)
      .neq('id', fixture.id)
      .order('fixture_date', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.players_per_side && data.players_per_side !== slots.starters.length) {
          fixture.players_per_side = data.players_per_side
          setSlotsState({ starters: Array(data.players_per_side).fill(null), subs: [] })
        }
      })
  }, [])

  function clearDraft() {
    try { localStorage.removeItem(draftKey) } catch (e) { /* ignore */ }
  }
  const [search, setSearch] = useState('')
  const [view, setView] = useState('list')
  const [swapFirst, setSwapFirst] = useState(null)
  const [emptySlotTarget, setEmptySlotTarget] = useState(null)
  const { starters, subs } = slots
  const assignedIds = [...starters.filter(Boolean).map(p => p.id), ...subs.map(p => p.id)]
  const filtered = players.filter(p => p.full_name.toLowerCase().includes(search.toLowerCase())).sort((a, b) => a.full_name.localeCompare(b.full_name))
  const positionNames = fixture.players_per_side ? getFormation(fixture.players_per_side).positionNames : (POSITION_NUMBERS[fixture.sport] || POSITION_NUMBERS.football)

  function getSlotIndex(id) { return starters.findIndex(p => p && p.id === id) }
  function getSubIndex(id) { return subs.findIndex(p => p.id === id) }

  function nextFreeSubNumber() {
    const startersCap = starters.length
    const taken = new Set([...starters.filter(Boolean), ...subs].map(p => p.assigned_number))
    let n = startersCap + 1
    while (taken.has(String(n))) n++
    return String(n)
  }

  function togglePlayer(p) {
    const slotIdx = getSlotIndex(p.id)
    const subIdx = getSubIndex(p.id)
    if (slotIdx !== -1) { const ns=[...starters]; ns[slotIdx]=null; setSlots({starters:ns,subs}); return }
    if (subIdx !== -1) { setSlots({starters,subs:subs.filter(x=>x.id!==p.id)}); return }
    const startersCap = starters.length
    const firstEmpty = starters.findIndex(x => x === null)
    if (firstEmpty !== -1 && starters.filter(Boolean).length < startersCap) {
      const ns=[...starters]; ns[firstEmpty]={...p, assigned_number:String(firstEmpty+1)}
      setSlots({starters:ns,subs})
    } else {
      setSlots({starters,subs:[...subs,{...p, assigned_number:nextFreeSubNumber()}]})
    }
  }

  function moveToSub(id) {
    const slotIdx=getSlotIndex(id); if(slotIdx===-1) return
    const player=starters[slotIdx]; const ns=[...starters]; ns[slotIdx]=null
    setSlots({starters:ns,subs:[...subs,{...player,assigned_number:nextFreeSubNumber()}]})
  }

  function moveToStarter(id) {
    const subIdx=getSubIndex(id); if(subIdx===-1) return
    const player=subs[subIdx]; const firstEmpty=starters.findIndex(x=>x===null)
    if(firstEmpty===-1){alert('No empty starter slots.');return}
    const ns=[...starters]; ns[firstEmpty]={...player,assigned_number:String(firstEmpty+1)}
    setSlots({starters:ns,subs:subs.filter(x=>x.id!==id)})
  }

  function updateNumber(id, newNum) {
    const numStr=String(newNum)
    const taken=[...starters.filter(p=>p&&p.id!==id),...subs.filter(p=>p.id!==id)].some(p=>p.assigned_number===numStr)
    if(taken){alert('Number ' + newNum + ' already taken.');return}
    setSlots({
      starters:starters.map(p=>p&&p.id===id?{...p,assigned_number:numStr}:p),
      subs:subs.map(p=>p.id===id?{...p,assigned_number:numStr}:p)
    })
  }

  function assignToSlot(player) {
    if(!emptySlotTarget) return
    const slotIdx=emptySlotTarget-1
    const numStr=String(emptySlotTarget)
    const conflict=[...starters.filter(p=>p&&p.id!==player.id),...subs.filter(p=>p.id!==player.id)].find(p=>p.assigned_number===numStr)
    const newSubs=getSubIndex(player.id)!==-1?subs.filter(x=>x.id!==player.id):subs
    const cleanedSubs=conflict?newSubs.map(p=>p.id===conflict.id?{...p,assigned_number:''}:p):newSubs
    const ns=[...starters]; ns[slotIdx]={...player,assigned_number:numStr}
    setSlots({starters:ns,subs:cleanedSubs}); setEmptySlotTarget(null)
  }

  function handlePitchSwap(player) {
    if(!swapFirst){setSwapFirst(player);return}
    if(swapFirst.id===player.id){setSwapFirst(null);return}
    const ns=[...starters]
    const idxA=ns.findIndex(p=>p&&p.id===swapFirst.id)
    const idxB=ns.findIndex(p=>p&&p.id===player.id)
    if(idxA!==-1&&idxB!==-1){
      const numA=ns[idxA].assigned_number; const numB=ns[idxB].assigned_number
      ns[idxA]={...ns[idxA],assigned_number:numB}; ns[idxB]={...ns[idxB],assigned_number:numA}
      setSlots({starters:ns,subs})
    }
    setSwapFirst(null)
  }

  function handleConfirm() {
    const filled = starters.filter(Boolean)
    if (filled.length < 15) { if (!window.confirm('Only ' + filled.length + ' starters. Proceed?')) return }
    const filledWithNums = filled.map(p => ({ ...p, jersey_number: p.jersey_number || p.assigned_number, match_number: p.assigned_number }))
    const subsWithNums = subs.map(p => ({ ...p, jersey_number: p.jersey_number || p.assigned_number, match_number: p.assigned_number }))
    clearDraft()
    onSave(filledWithNums, subsWithNums)
  }

  function availableNumbers(currentId) {
    const used=[...starters.filter(p=>p&&p.id!==currentId),...subs.filter(p=>p.id!==currentId)].map(p=>p.assigned_number)
    return Array.from({length:30},(_,i)=>String(i+1)).filter(n=>!used.includes(n))
  }

  const filledCount=starters.filter(Boolean).length
  const startersCap = starters.length
  const squadGrade = fixture.squads?.current_grade || fixture.squads?.age_group
  const smallSidedAllowed = allowsSmallSided(squadGrade)

  async function changeTeamSize(newSize) {
    if (newSize === startersCap) return
    if (!window.confirm('Change to ' + newSize + '-a-side? This will reset your current starter/sub selections.')) return
    await supabase.from('fixtures').update({ players_per_side: newSize }).eq('id', fixture.id)
    fixture.players_per_side = newSize
    setSlots({ starters: Array(newSize).fill(null), subs: [] })
    clearDraft()
  }

  return (
    <div>
      <div style={{ background: '#185FA5', color: 'white', padding: '16px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'white', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <h2 style={{ fontSize: '17px', fontWeight: '500' }}>Select Squad</h2>
          <button disabled={filledCount===0}
            style={{ padding: '8px 14px', background: filledCount>0?'white':'rgba(255,255,255,0.3)', color: filledCount>0?'#185FA5':'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: filledCount>0?'pointer':'default' }}
            onClick={handleConfirm}>✓ Confirm {filledCount>0?'('+filledCount+')':''}</button>
        </div>
        <p style={{ fontSize: '12px', opacity: 0.8 }}>vs {fixture.opponent} · {filledCount}/{startersCap} starters · {subs.length} subs</p>
        {smallSidedAllowed && (
          <div style={{ marginTop: '10px' }}>
            <label style={{ fontSize: '11px', opacity: 0.8, display: 'block', marginBottom: '4px' }}>Players per side</label>
            <select value={startersCap} onChange={e => changeTeamSize(Number(e.target.value))}
              style={{ padding: '6px 10px', borderRadius: '8px', border: 'none', fontSize: '13px', background: 'white', color: '#185FA5' }}>
              {[9,10,11,12,13,14,15].map(n => <option key={n} value={n}>{n}-a-side</option>)}
            </select>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          {['list','pitch'].map(v=>(
            <button key={v} onClick={()=>setView(v)}
              style={{ padding: '6px 14px', borderRadius: '20px', border: 'none', fontSize: '13px', cursor: 'pointer', fontWeight: '500', background: view===v?'white':'rgba(255,255,255,0.2)', color: view===v?'#185FA5':'white' }}>
              {v==='list'?'📋 List':'🏟️ Pitch'}
            </button>
          ))}
        </div>
      </div>

      {view === 'pitch' && (
        <div style={{ padding: '12px 16px' }}>
          <div style={{ background: '#E6F1FB', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px', fontSize: '13px', color: '#185FA5' }}>
            {emptySlotTarget ? 'Select player for ' + positionNames[emptySlotTarget] + ' (#' + emptySlotTarget + ')' : swapFirst ? 'Swapping ' + swapFirst.full_name : 'Tap empty slot to assign. Tap two players to swap.'}
          </div>
          <PitchDisplay lineup={starters.filter(Boolean)} sport={fixture.sport} playersPerSide={fixture.players_per_side}
            onPlayerTap={emptySlotTarget?()=>{}:handlePitchSwap}
            swapFirst={swapFirst} emptySlots={true} onEmptySlotTap={setEmptySlotTarget} />
          {emptySlotTarget && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <p style={{ fontSize: '13px', fontWeight: '500', color: '#185FA5' }}>Select player for {positionNames[emptySlotTarget]}</p>
                <button onClick={() => setEmptySlotTarget(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#888' }}>✕</button>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                {players.filter(p=>!assignedIds.includes(p.id)).sort((a,b)=>a.full_name.localeCompare(b.full_name)).map(p=>(
                  <div key={p.id} onClick={()=>assignToSlot(p)} style={{ padding: '10px 12px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer', fontSize: '14px' }}>{p.full_name}</div>
                ))}
              </div>
            </div>
          )}
          {subs.length > 0 && !emptySlotTarget && (
            <div style={{ marginTop: '12px' }}>
              <p style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>Substitutes</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {subs.map(p=>(
                  <button key={p.id} onClick={()=>handlePitchSwap(p)}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid ' + (swapFirst?.id===p.id?'#FFD700':'#533AB7'), background: swapFirst?.id===p.id?'#FFF9E6':'#EEEDFE', fontSize: '12px', cursor: 'pointer', color: '#533AB7', fontWeight: '500' }}>
                    #{p.jersey_number||p.assigned_number} {p.full_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'list' && (
        <>
          <div style={{ padding: '10px 16px', borderBottom: '0.5px solid #e0e0e0', position: 'sticky', top: 0, background: 'white', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f5', borderRadius: '10px', padding: '8px 12px', gap: '8px' }}>
              <span>🔍</span>
              <input type="text" placeholder="Search players..." value={search} onChange={e=>setSearch(e.target.value)}
                style={{ border: 'none', background: 'none', fontSize: '14px', flex: 1, outline: 'none' }} />
            </div>
          </div>
          {assignedIds.length > 0 && (
            <div style={{ padding: '8px 16px', background: '#E6F1FB', borderBottom: '0.5px solid #c8dff5' }}>
              <p style={{ fontSize: '11px', color: '#185FA5', fontWeight: '500', marginBottom: '6px' }}>SELECTED ({filledCount} starters, {subs.length} subs)</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {starters.map(p=>p?(<button key={p.id} onClick={()=>togglePlayer(p)} style={{ padding: '4px 10px', borderRadius: '20px', background: '#185FA5', color: 'white', border: 'none', fontSize: '12px', cursor: 'pointer' }}>#{p.jersey_number||p.assigned_number} {p.full_name.split(' ').slice(-1)[0]} ✕</button>):null)}
                {subs.map(p=>(<button key={p.id} onClick={()=>togglePlayer(p)} style={{ padding: '4px 10px', borderRadius: '20px', background: '#533AB7', color: 'white', border: 'none', fontSize: '12px', cursor: 'pointer' }}>#{p.jersey_number||p.assigned_number} {p.full_name.split(' ').slice(-1)[0]} ✕</button>))}
              </div>
            </div>
          )}
          {filtered.map(p => {
            const slotIdx=getSlotIndex(p.id); const subIdx=getSubIndex(p.id)
            const isStarter=slotIdx!==-1; const isSub=subIdx!==-1; const isSelected=isStarter||isSub
            const selPlayer=isStarter?starters[slotIdx]:isSub?subs[subIdx]:null
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: '0.5px solid #f0f0f0', background: isStarter?'#E6F1FB':isSub?'#EEEDFE':'white' }}>
                <div onClick={()=>togglePlayer(p)} style={{ width: '32px', height: '32px', borderRadius: '50%', background: isStarter?'#185FA5':isSub?'#533AB7':'#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '500', color: isSelected?'white':'#888', cursor: 'pointer', flexShrink: 0 }}>{isSelected?'✓':''}</div>
                <div onClick={()=>togglePlayer(p)} style={{ flex: 1, cursor: 'pointer' }}>
                  <p style={{ fontSize: '14px' }}>{p.full_name}</p>
                  <p style={{ fontSize: '11px', color: '#888' }}>#{p.jersey_number || '—'} · {p.position||'No position'}</p>
                </div>
                {isSelected&&selPlayer&&(<select value={selPlayer.assigned_number||''} onChange={e=>updateNumber(p.id,e.target.value)} style={{ width: '70px', padding: '4px', border: '1px solid #185FA5', borderRadius: '6px', fontSize: '13px', background: 'white', cursor: 'pointer' }}><option value="">Pos</option>{availableNumbers(p.id).map(n=><option key={n} value={n}>#{n}</option>)}</select>)}
                {isStarter&&<button onClick={()=>moveToSub(p.id)} style={{ fontSize: '11px', padding: '3px 7px', borderRadius: '6px', border: '0.5px solid #533AB7', background: '#EEEDFE', color: '#533AB7', cursor: 'pointer', whiteSpace: 'nowrap' }}>→ Sub</button>}
                {isSub&&<button onClick={()=>moveToStarter(p.id)} style={{ fontSize: '11px', padding: '3px 7px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer', whiteSpace: 'nowrap' }}>→ Start</button>}
              </div>
            )
          })}
          <div style={{ padding: '16px' }}>
            <button disabled={filledCount===0} style={{ display: 'block', width: '100%', padding: '14px', background: filledCount>0?'#185FA5':'#ccc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', cursor: filledCount>0?'pointer':'default' }} onClick={handleConfirm}>
              Confirm — {filledCount} starters{subs.length>0?', ' + subs.length + ' subs':''}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function Modal({ title, onClose, children }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalBoxStyle} onClick={e=>e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ fontSize: '16px', fontWeight: '500' }}>{title}</h3>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', maxHeight: '60vh' }}>{children}</div>
      </div>
    </div>
  )
}

function PlayerRow({ player, onTap, label, labelColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer' }} onClick={onTap}>
      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '500', color: '#185FA5', flexShrink: 0 }}>
        {player.match_number || player.jersey_number || player.assigned_number || '—'}
      </div>
      <span style={{ flex: 1, fontSize: '15px' }}>{player.full_name}</span>
      {label && <span style={{ fontSize: '13px', color: labelColor, fontWeight: '500' }}>{label}</span>}
    </div>
  )
}

const clockBtn = { fontSize: '11px', padding: '4px 9px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.15)', color: 'white', cursor: 'pointer' }
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end', zIndex: 200 }
const modalBoxStyle = { background: 'white', borderRadius: '16px 16px 0 0', width: '100%', maxHeight: '80vh', overflowY: 'auto' }
const modalHeaderStyle = { padding: '16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'white' }
const closeBtn = { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#888' }
function scoreBtn(color, bg) { return { width: '38px', height: '38px', borderRadius: '10px', border: '0.5px solid ' + color, background: bg, color, fontSize: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, flexShrink: 0 } }
function evBtn(color, bg) { return { padding: '11px 6px', borderRadius: '10px', border: '0.5px solid ' + color, background: bg, color, fontSize: '12px', cursor: 'pointer', textAlign: 'center', fontWeight: '500', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', whiteSpace: 'pre-line' } }
function modalActionBtn(color) { return { padding: '13px', borderRadius: '10px', border: '0.5px solid ' + color, background: color + '15', color, cursor: 'pointer', fontSize: '14px', fontWeight: '500' } }
function resetBtnStyle(color) { return { padding: '14px', borderRadius: '10px', border: '0.5px solid ' + color, background: color + '15', color, cursor: 'pointer', fontSize: '14px', fontWeight: '500', textAlign: 'left' } }